import axios from "axios";
import * as React from "react";
import * as ReactQuery from "react-query";

import {
  GenericDataSource_CapableOfBeingIntersected,
  GraphqlWrappedData,
  InitialStateMinimumRequirements_GraphQl,
} from "../../../Common";

import {
  CheckoutModel_MyBag,
  CheckoutModel_MyBag_Graphql_Request,
} from "../../model";

import { useGenericDataPortForMyBag } from "../use-generic-data-port-for-mybag";

import { DataMutationMyBag_ItemRemove_QueryKey } from "./data-mutation--mybag-item-remove-query-key-factory";

export interface DataMutationMyBagItemRemoveConsumerInterface
  extends GenericDataSource_CapableOfBeingIntersected<
    DataMutationMyBagItemRemove_Result["data"]
  > {
  data: DataMutationMyBagItemRemove_Result["data"];
  request: DataMutationMyBagItemRemove_Result["variables"];
  status: DataMutationMyBagItemRemove_Result["status"];
}
const defaultValue =
  {} as unknown as DataMutationMyBagItemRemoveConsumerInterface;
const DataMutationMyBagItemRemoveContext = React.createContext(defaultValue);

export interface DataMutationMyBagItemRemove_ControllerInterface {
  mutate: DataMutationMyBagItemRemove_Result["mutate"];
  mutateAsync: DataMutationMyBagItemRemove_Result["mutateAsync"];
}
const controllerDefaultValue =
  {} as unknown as DataMutationMyBagItemRemove_ControllerInterface;
const DataMutationMyBagItemRemove_ControllerContext = React.createContext(
  controllerDefaultValue
);

export interface DataMutationMyBagItemRemove_Request {
  cartId: string;
  cartItemToRemoveId: string;
}
export type DataMutationMyBagItemRemove_Result = ReactQuery.UseMutationResult<
  CheckoutModel_MyBag,
  unknown,
  DataMutationMyBagItemRemove_Request,
  unknown
>;

export type GraphqlResponse_MyBagItemRemove = GraphqlWrappedData<
  "removeItemFromCart",
  { cart: CheckoutModel_MyBag }
>;

export interface DataMutationMyBagItemRemoveProviderProps {
  useInitialState: () => {
    initialState: InitialStateMinimumRequirements_GraphQl;
  };
}
export const DataMutationMyBagItemRemoveProvider: React.FunctionComponent<
  DataMutationMyBagItemRemoveProviderProps
> = (props) => {
  const { initialState } = props.useInitialState();

  const [genericDataPortValues, genericDataPortController] =
    useGenericDataPortForMyBag();

  const mutationKey: ReactQuery.MutationKey = React.useMemo(() => {
    return DataMutationMyBag_ItemRemove_QueryKey.mybagItemRemove();
  }, []);

  const mutationFn: ReactQuery.MutationFunction<
    CheckoutModel_MyBag,
    DataMutationMyBagItemRemove_Request
  > = React.useCallback(
    async (request) => {
      const gqlQuery = `
        mutation {
          removeItemFromCart(
            input: {
                cart_id: "${request.cartId}",
                cart_item_id: ${request.cartItemToRemoveId}
            }
          ) {
            cart {
              ${CheckoutModel_MyBag_Graphql_Request}
            }
          }
        }
      `;

      const headers = {
        "Content-Type": "application/json",
        Store: initialState.store_code,
      };

      const { data } = await axios.post<GraphqlResponse_MyBagItemRemove>(
        "/graphql",
        { query: gqlQuery },
        { headers }
      );

      genericDataPortController.portError(data);

      const { data: gqlResponseData } = data || {};
      const { removeItemFromCart } = gqlResponseData || {};
      const { cart } = removeItemFromCart || {};

      if ((window as any).getMiniCart as any) {
        ((window as any).getMiniCart as any)((customerData: any) => {
          const cart = customerData.get("cart");
          customerData.reload(["cart"], true);
        });
      }

      genericDataPortController.portSuccess();

      return cart;
    },
    [initialState, genericDataPortController]
  );

  const mutation = ReactQuery.useMutation(mutationKey, mutationFn, {
    onSuccess: genericDataPortController.useMutationOrQueryOnSuccess,
  });

  const value: DataMutationMyBagItemRemoveConsumerInterface = React.useMemo(
    () => ({
      data: mutation.data,
      error: mutation.error,
      request: mutation.variables,
      status: mutation.status,

      dataUpdatedAt: genericDataPortValues.dataUpdatedAt,
      errorUpdatedAt: genericDataPortValues.errorUpdatedAt,
    }),
    [
      mutation.data,
      mutation.variables,
      mutation.status,
      mutation.error,

      genericDataPortValues.dataUpdatedAt,
      genericDataPortValues.errorUpdatedAt,
    ]
  );
  const controllerValue: DataMutationMyBagItemRemove_ControllerInterface =
    React.useMemo(
      () => ({
        mutate: mutation.mutate,
        mutateAsync: mutation.mutateAsync,
      }),
      [mutation, mutation.mutateAsync]
    );
  return (
    <DataMutationMyBagItemRemoveContext.Provider value={value}>
      <DataMutationMyBagItemRemove_ControllerContext.Provider
        value={controllerValue}
      >
        {props.children || []}
      </DataMutationMyBagItemRemove_ControllerContext.Provider>
    </DataMutationMyBagItemRemoveContext.Provider>
  );
};

export const useDataMutationMyBagItemRemove = () => {
  const context = React.useContext(DataMutationMyBagItemRemoveContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataMutationMyBagItemRemove must be used within a DataMutationMyBagItemRemoveProvider"
    );
  }
  return context;
};

export const useDataMutationMyBagItemRemove_asController = () => {
  const context = React.useContext(
    DataMutationMyBagItemRemove_ControllerContext
  );
  if (context === controllerDefaultValue) {
    throw new Error(
      "useDataMutationMyBagItemRemove_asController must be used within a DataMutationMyBagItemRemoveProvider"
    );
  }
  return context;
};
